import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import minilogo from '../assets/minilogo.png';

const LoaderAnimation = ({ onComplete }) => {
  const [phase, setPhase] = useState(0); // 0: initial, 1: logo reveal, 2: fade out
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Phases timing
    const t1 = setTimeout(() => setPhase(1), 500);
    const t2 = setTimeout(() => setPhase(2), 2800);
    const t3 = setTimeout(() => onComplete && onComplete(), 3400);

    // Progress bar simulation
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 100;
        return prev + Math.random() * 20;
      });
    }, 150);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearInterval(interval);
    };
  }, [onComplete]);

  return (
    <AnimatePresence>
      {phase < 2 && (
        <motion.div
          key="loader-container"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.05, filter: 'blur(15px)' }}
          transition={{ duration: 0.6, ease: [0.43, 0.13, 0.23, 0.96] }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: '#000',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {/* Animated Background Glows */}
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.1, 0.25, 0.1],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute',
              width: '500px',
              height: '500px',
              background: 'radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)',
              filter: 'blur(80px)',
              zIndex: 0,
            }}
          />

          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Mini Logo Pulse Focus */}
            <motion.div
              initial={{ scale: 0.4, opacity: 0 }}
              animate={phase >= 1 ? { scale: 1, opacity: 1 } : {}}
              transition={{
                type: 'spring',
                stiffness: 120,
                damping: 18,
                delay: 0.1
              }}
              style={{ position: 'relative' }}
            >
              <motion.img
                src={minilogo}
                alt="SnapED"
                style={{ width: 100, height: 100, objectFit: 'contain', filter: 'drop-shadow(0 0 20px rgba(34, 197, 94, 0.4))' }}
                animate={{
                  y: [0, -12, 0],
                  filter: [
                    'drop-shadow(0 0 15px rgba(34, 197, 94, 0.3))',
                    'drop-shadow(0 0 35px rgba(34, 197, 94, 0.6))',
                    'drop-shadow(0 0 15px rgba(34, 197, 94, 0.3))'
                  ]
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Complex Expansion Rings */}
              {[...Array(2)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{ scale: [1, 2], opacity: [0.4, 0] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'easeOut',
                    delay: i * 1.25
                  }}
                  style={{
                    position: 'absolute',
                    inset: -5,
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    borderRadius: '50%',
                  }}
                />
              ))}
            </motion.div>

            {/* Sub-text Reveal */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={phase >= 1 ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8, duration: 0.7 }}
              style={{ marginTop: 32, textAlign: 'center' }}
            >
              <div style={{ position: 'relative', overflow: 'hidden' }}>
                <p style={{
                  color: '#fff',
                  fontSize: '0.9rem',
                  letterSpacing: '0.6em',
                  fontWeight: 900,
                  textTransform: 'uppercase',
                  margin: 0,
                  opacity: 0.9
                }}>
                  Snap<span style={{ color: '#22c55e' }}>ED</span>
                </p>
                <div style={{
                  height: 1,
                  width: '100%',
                  background: 'linear-gradient(90deg, transparent, #22c55e, transparent)',
                  marginTop: 4
                }} />
                <p style={{
                  color: '#22c55e',
                  fontSize: '0.6rem',
                  letterSpacing: '0.4em',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  marginTop: 6,
                  opacity: 0.6
                }}>
                  codeCampus
                </p>
              </div>
            </motion.div>
          </div>

          {/* Minimalist Progress Line */}
          <div style={{
            position: 'absolute',
            bottom: '12%',
            width: '180px',
            height: '1px',
            background: 'rgba(255,255,255,0.05)',
          }}>
            <motion.div
              initial={{ width: '0%', left: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ ease: 'linear' }}
              style={{
                position: 'absolute',
                top: 0,
                height: '100%',
                background: '#22c55e',
                boxShadow: '0 0 10px #22c55e',
              }}
            />
          </div>

          <motion.div
            style={{
              position: 'absolute',
              bottom: 'calc(12% - 20px)',
              color: 'rgba(34, 197, 94, 0.5)',
              fontSize: '0.55rem',
              fontWeight: 800,
              letterSpacing: '0.2em'
            }}
          >
            {Math.round(progress)}%
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoaderAnimation;
